/**
 * One message in the thread, drawn by its kind: the canon's bubble, picture, film, voice note and
 * five cards (`docs/design/czat-web-2026-10-05/`, board C1), with the time under the last of a run
 * and „Nie wysłano · Ponów · Usuń" under a send that failed.
 *
 * Every card's act leads to the screen this app already has for it — the exercise on `/cwiczenia`,
 * the plan on the client's plan screen, the session on the client's card (`?exec=`), the trainer's
 * own recording request to the client's Forma tab — and the client's recording opens the one clip
 * player over the chat (`onOpenClip`). Nothing here navigates anywhere the trainer did not press.
 */

import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { muscleGroupFromApi, muscleGroupLabelKey } from '~/entities/exercise-library';
import photoBox from '~/shared/ui/photoBox.module.css';

import { mediaStatusOf, type ChatMessage } from '../model/chatMessage';
import { splitLinks } from '../model/messageLinks';
import { clockOf } from '../model/threadItems';

import {
  AlertGlyph,
  ArrowGlyph,
  CalendarGlyph,
  CameraGlyph,
  ClockGlyph,
  DoubleTickGlyph,
  DumbbellGlyph,
  LayersGlyph,
  PlayGlyph,
} from './ChatGlyphs';
import { useSignedUrl } from '../lib/useSignedUrl';
import { VoiceBubble } from './VoiceBubble';
import styles from './chat.module.css';

/** What the lightbox is asked to show. */
export type MediaView =
  | { readonly kind: 'image'; readonly mediaId: number | null; readonly url: string }
  | {
      readonly kind: 'video';
      readonly mediaId: number | null;
      readonly url: string;
      readonly poster: string | null;
    };

interface MessageBubbleProps {
  readonly message: ChatMessage;
  readonly first: boolean;
  readonly last: boolean;
  /** Whether the bubble arrived while the thread was on screen, and so plays its entrance. */
  readonly entering: boolean;
  /** The client of this conversation, for the cards that lead to their own screens. */
  readonly customerId: number | null;
  readonly theirName: string;
  readonly onOpenMedia: (view: MediaView) => void;
  readonly onOpenClip: (clipId: number) => void;
  readonly onRetry: (clientId: string) => void;
  readonly onRemove: (clientId: string) => void;
}

/** A card's muscle group in the reader's words: the wire sends the backend's own name. */
function useMuscleLabel(): (value: string | null | undefined) => string {
  const { t } = useTranslation();
  return (value) => {
    if (value === null || value === undefined || value === '') return '';
    const group = muscleGroupFromApi(value);
    return group === null ? value : t(muscleGroupLabelKey(group));
  };
}

/** A plan's category in the reader's words (`planTemplates.category.*`, the mobile app's copy). */
function planCategoryLabel(t: (key: string, options?: Record<string, unknown>) => string, category: string | null | undefined): string {
  if (category === null || category === undefined || category === '') return '';
  return t(`planTemplates.category.${category}`, { defaultValue: category });
}

function cx(...names: (string | false | null | undefined)[]): string {
  return names.filter(Boolean).join(' ');
}

export function MessageBubble({
  message,
  first,
  last,
  entering,
  customerId,
  theirName,
  onOpenMedia,
  onOpenClip,
  onRetry,
  onRemove,
}: MessageBubbleProps) {
  const { t, i18n } = useTranslation();
  const mine = message.mine;
  const failed = message.status === 'failed';
  const processingFailed = mediaStatusOf(message) === 'Failed' && message.local === null;

  if (message.system) {
    return (
      <div className={styles.system} role="article" aria-label={t('web:chat.message.system')}>
        <LinkedText text={message.text} />
        <span className={cx(styles.num, styles.systemTime)}>{clockOf(message.createdAt, i18n.language)}</span>
      </div>
    );
  }

  return (
    <div
      className={cx(
        styles.msg,
        mine ? styles.mine : styles.theirs,
        first && styles.first,
        last && styles.last,
        entering && styles.enter,
        failed && styles.failed,
      )}
      role="article"
      aria-label={mine ? t('web:chat.message.mine') : t('web:chat.message.theirs', { name: theirName })}
    >
      <div className={styles.body}>
        <MessageContent
          message={message}
          customerId={customerId}
          theirName={theirName}
          onOpenMedia={onOpenMedia}
          onOpenClip={onOpenClip}
        />
        {last && !failed ? (
          <div className={styles.meta}>
            <span className={styles.num}>{clockOf(message.createdAt, i18n.language)}</span>
            {mine && message.status === 'sending' ? (
              <span className={styles.tick} role="img" aria-label={t('web:chat.message.sending')}>
                <ClockGlyph />
              </span>
            ) : null}
            {mine && message.status === 'sent' ? (
              <span className={styles.tick} role="img" aria-label={t('web:chat.message.sent')}>
                <DoubleTickGlyph />
              </span>
            ) : null}
          </div>
        ) : null}
        {failed ? (
          <div className={styles.failRow}>
            <AlertGlyph />
            <span>{t('chat.fail.label')}</span>
            <button type="button" onClick={() => { onRetry(message.clientId); }}>
              {t('chat.fail.retry')}
            </button>
            <span className={styles.failSep} aria-hidden="true" />
            <button type="button" onClick={() => { onRemove(message.clientId); }}>
              {t('chat.fail.delete')}
            </button>
          </div>
        ) : null}
        {processingFailed ? (
          <div className={styles.failRow}>
            <AlertGlyph />
            <span>{t('chat.videoFail.label')}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface ContentProps {
  readonly message: ChatMessage;
  readonly customerId: number | null;
  readonly theirName: string;
  readonly onOpenMedia: (view: MediaView) => void;
  readonly onOpenClip: (clipId: number) => void;
}

function MessageContent({ message, customerId, theirName, onOpenMedia, onOpenClip }: ContentProps) {
  const { t } = useTranslation();
  switch (message.kind) {
    case 'text':
      return (
        <div className={styles.bubble}>
          <LinkedText text={message.text} />
        </div>
      );
    case 'image':
      return <ImageContent message={message} theirName={theirName} onOpenMedia={onOpenMedia} />;
    case 'video':
      return <VideoContent message={message} onOpenMedia={onOpenMedia} />;
    case 'voice':
      return <VoiceBubble message={message} />;
    case 'exercise':
      return <ExerciseCard message={message} />;
    case 'plan':
      return <PlanCard message={message} customerId={customerId} />;
    case 'summary':
      return <SummaryCard message={message} customerId={customerId} />;
    case 'clipRequest':
      return <ClipRequestCard message={message} customerId={customerId} />;
    case 'clip':
      return <ClipCard message={message} onOpenClip={onOpenClip} />;
    case 'unsupported':
      return (
        <div className={cx(styles.bubble, styles.unsupported)}>
          <b>{t('chat.window.unsupportedTitle')}</b>
          <span>{t('chat.window.unsupportedBody')}</span>
        </div>
      );
  }
}

/** A text with its `https://` addresses as links that open in a new tab; the host is the hover hint. */
function LinkedText({ text }: { readonly text: string }) {
  const { t } = useTranslation();
  return splitLinks(text).map((segment, index) =>
    segment.kind === 'text' ? (
      segment.text
    ) : (
      <a
        // The segments of one text never reorder, so the position is a stable key.
        key={index}
        className={styles.link}
        href={segment.href}
        target="_blank"
        rel="noopener noreferrer"
        title={segment.host}
        aria-label={t('web:chat.message.link', { label: segment.host })}
      >
        {segment.text}
      </a>
    ),
  );
}

/** Upload state over a picture or a film: a stage line and a progress bar. */
function UploadVeil({ message }: { readonly message: ChatMessage }) {
  const { t } = useTranslation();
  const local = message.local;
  const processing = mediaStatusOf(message) === 'Processing';
  if (local === null && !processing) return null;
  if (local?.status === 'failed') return null;
  const stage = local?.stage ?? (processing ? 'processing' : 'sending');
  const label =
    stage === 'compressing'
      ? t('chat.window.videoCompressing')
      : stage === 'processing'
        ? t('chat.window.videoProcessing')
        : t('chat.window.videoSending');
  const progress = local?.progress ?? null;
  return (
    <span className={styles.upload}>
      <span className={styles.uploadLabel}>
        {label}
        {stage === 'sending' && progress !== null ? ` ${String(Math.round(progress * 100))}%` : ''}
      </span>
      {progress !== null ? (
        <span className={styles.uploadBar}>
          <i style={{ width: `${String(Math.round(progress * 100))}%` }} />
        </span>
      ) : null}
    </span>
  );
}

function Caption({ message }: { readonly message: ChatMessage }) {
  if (message.text === '') return null;
  return (
    <div className={cx(styles.bubble, styles.caption)}>
      <LinkedText text={message.text} />
    </div>
  );
}

function ImageContent({
  message,
  theirName,
  onOpenMedia,
}: {
  readonly message: ChatMessage;
  readonly theirName: string;
  readonly onOpenMedia: (view: MediaView) => void;
}) {
  const { t } = useTranslation();
  const media = message.wire.media;
  const signed = useSignedUrl(media?.mediaId, media?.url, 'primary');
  const src = message.previewUrl ?? signed.url;
  return (
    <div className={styles.mediaWrap}>
      {src === null ? (
        <div className={cx(styles.bubble, styles.gone)}>{t('chat.window.imageUnavailable')}</div>
      ) : (
        <button
          className={styles.media}
          type="button"
          aria-label={t('web:chat.media.openImage')}
          onClick={() => {
            onOpenMedia({ kind: 'image', mediaId: media?.mediaId ?? null, url: src });
          }}
        >
          <img
            className={cx(String(photoBox.photo), styles.mediaPicture)}
            src={src}
            alt={message.mine ? t('web:chat.media.yourImage') : t('web:chat.media.imageFrom', { name: theirName })}
            decoding="async"
            onError={message.previewUrl === null ? signed.onError : undefined}
          />
          <UploadVeil message={message} />
        </button>
      )}
      <Caption message={message} />
    </div>
  );
}

function VideoContent({
  message,
  onOpenMedia,
}: {
  readonly message: ChatMessage;
  readonly onOpenMedia: (view: MediaView) => void;
}) {
  const { t } = useTranslation();
  const video = message.wire.video;
  const poster = useSignedUrl(video?.mediaId, video?.posterUrl, 'poster');
  const ready = video?.status === 'Ready' && typeof video.videoUrl === 'string';
  const previewUrl = message.previewUrl;
  const open = () => {
    if (ready && typeof video.videoUrl === 'string') {
      onOpenMedia({ kind: 'video', mediaId: video.mediaId ?? null, url: video.videoUrl, poster: poster.url });
    } else if (previewUrl !== null) {
      onOpenMedia({ kind: 'video', mediaId: null, url: previewUrl, poster: null });
    }
  };
  return (
    <div className={styles.mediaWrap}>
      <button
        className={styles.media}
        type="button"
        aria-label={t('web:chat.media.playVideo')}
        onClick={open}
      >
        {poster.url !== null ? (
          <img
            className={cx(String(photoBox.photo), styles.mediaPicture)}
            src={poster.url}
            alt=""
            decoding="async"
            onError={poster.onError}
          />
        ) : previewUrl !== null ? (
          <video className={styles.mediaPicture} src={previewUrl} muted playsInline preload="metadata" />
        ) : (
          <span className={cx(styles.mediaPicture, styles.mediaBlank)} />
        )}
        <span className={styles.playDot} aria-hidden="true">
          <PlayGlyph size={22} />
        </span>
        <UploadVeil message={message} />
      </button>
      <Caption message={message} />
    </div>
  );
}

function CardTag({ icon, label }: { readonly icon: ReactNode; readonly label: string }) {
  return (
    <div className={styles.cardTag}>
      {icon}
      <span>{label}</span>
    </div>
  );
}

function CardRow({
  image,
  title,
  meta,
}: {
  readonly image: string | null | undefined;
  readonly title: string;
  readonly meta: string;
}) {
  return (
    <div className={styles.cardRow}>
      {typeof image === 'string' ? (
        <img className={cx(String(photoBox.photo), styles.thumb)} src={image} alt="" decoding="async" />
      ) : (
        <span className={cx(styles.thumb, styles.thumbBlank)} aria-hidden="true">
          <DumbbellGlyph size={18} />
        </span>
      )}
      <div className={styles.cardText}>
        <div className={styles.cardTitle}>{title}</div>
        {meta === '' ? null : <div className={styles.cardMeta}>{meta}</div>}
      </div>
    </div>
  );
}

function ExerciseCard({ message }: { readonly message: ChatMessage }) {
  const { t } = useTranslation();
  const muscle = useMuscleLabel();
  const card = message.wire.exerciseCard;
  const name = card?.name ?? message.wire.content ?? '';
  return (
    <div className={styles.card} aria-label={t('chat.rich.exerciseCardA11y', { title: name })} role="group">
      <CardTag icon={<DumbbellGlyph width={2} />} label={t('chat.rich.exerciseTag')} />
      <CardRow image={card?.coverImageUrl ?? card?.posterUrl} title={name} meta={muscle(card?.primaryMuscleGroup)} />
      {typeof card?.exerciseId === 'number' && card.exerciseId > 0 ? (
        <Link className={styles.cta} to="/cwiczenia" search={{ cwiczenie: card.exerciseId }}>
          {t('chat.rich.viewExercise')}
          <ArrowGlyph />
        </Link>
      ) : null}
    </div>
  );
}

function PlanCard({ message, customerId }: { readonly message: ChatMessage; readonly customerId: number | null }) {
  const { t } = useTranslation();
  const plan = message.wire.trainingPlan;
  const name = plan?.name ?? message.wire.content ?? '';
  const meta = [
    planCategoryLabel(t, plan?.category),
    typeof plan?.phaseCount === 'number' && plan.phaseCount > 0
      ? t('chat.rich.planMeta.phases', { count: plan.phaseCount })
      : null,
  ]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
  const assignmentId = plan?.planAssignmentId;
  return (
    <div className={cx(styles.card, styles.planCard)} aria-label={t('chat.rich.planCardA11y', { title: name })} role="group">
      <div className={styles.cover}>
        {typeof plan?.coverImageUrl === 'string' ? (
          <img className={cx(String(photoBox.photo), styles.coverPicture)} src={plan.coverImageUrl} alt="" decoding="async" />
        ) : (
          <span className={cx(styles.coverPicture, styles.mediaBlank)} />
        )}
        <span className={styles.coverTag}>
          <LayersGlyph size={12} width={2} />
          {t('chat.rich.planTag')}
        </span>
      </div>
      <div className={styles.planBody}>
        <div className={styles.cardTitle}>{name}</div>
        {meta === '' ? null : <div className={styles.cardMeta}>{meta}</div>}
        {customerId !== null && typeof assignmentId === 'number' && assignmentId > 0 ? (
          <Link
            className={styles.cta}
            to="/klienci/$clientId/plan/$assignmentId"
            params={{ clientId: String(customerId), assignmentId: String(assignmentId) }}
          >
            {t('chat.rich.openPlan')}
            <ArrowGlyph />
          </Link>
        ) : null}
      </div>
    </div>
  );
}

function weightLabel(kg: number | null | undefined, reps: number | null | undefined, locale: string): string {
  const parts: string[] = [];
  if (typeof kg === 'number' && kg > 0) {
    parts.push(`${new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(kg)} kg`);
  }
  if (typeof reps === 'number' && reps > 0) parts.push(String(reps));
  return parts.join(' × ');
}

function SummaryCard({ message, customerId }: { readonly message: ChatMessage; readonly customerId: number | null }) {
  const { t, i18n } = useTranslation();
  const summary = message.wire.workoutSummary;
  const title = summary?.title ?? message.wire.content ?? '';
  const highlights = summary?.highlights ?? [];
  const executionId = summary?.workoutExecutionId;
  const reflection = summary?.reflection?.trim() ?? '';
  return (
    <div className={styles.card} aria-label={t('chat.rich.summaryCardA11y', { title })} role="group">
      <CardTag icon={<CalendarGlyph width={2} />} label={t('chat.rich.summaryTag')} />
      <div className={styles.summaryHead}>
        <div className={styles.cardTitle}>{title}</div>
        {typeof summary?.setsTotal === 'number' && summary.setsTotal > 0 ? (
          <span className={cx(styles.sets, styles.num)}>
            {t('chat.rich.setsChip', { done: summary.setsDone ?? 0, total: summary.setsTotal })}
          </span>
        ) : null}
      </div>
      {highlights.map((highlight, index) => (
        <div className={styles.highlight} key={`${String(index)}-${highlight.exerciseName ?? ''}`}>
          <span className={styles.highlightName}>{highlight.exerciseName ?? ''}</span>
          <span className={cx(styles.highlightTop, styles.num)}>
            {weightLabel(highlight.topWeightKg, highlight.topReps, i18n.language)}
          </span>
          {highlight.isPr === true ? (
            <span className={styles.pr} aria-label={t('chat.rich.prA11y')}>
              {t('chat.rich.prBadge')}
            </span>
          ) : null}
        </div>
      ))}
      {reflection === '' ? null : <div className={styles.reflection}>„{reflection}”</div>}
      {customerId !== null && typeof executionId === 'number' && executionId > 0 ? (
        <Link
          className={styles.cta}
          to="/klienci/$clientId"
          params={{ clientId: String(customerId) }}
          search={{ exec: executionId }}
        >
          {t('chat.rich.viewSession')}
          <ArrowGlyph />
        </Link>
      ) : null}
    </div>
  );
}

function ClipRequestCard({ message, customerId }: { readonly message: ChatMessage; readonly customerId: number | null }) {
  const { t } = useTranslation();
  const muscle = useMuscleLabel();
  const request = message.wire.exerciseClipRequest;
  const exercise = request?.exercise ?? null;
  const name = exercise?.name ?? message.wire.content ?? '';
  const available = request?.exerciseAvailable !== false;
  return (
    <div className={styles.card} aria-label={t('chat.rich.clipRequestCardA11y', { title: name })} role="group">
      <CardTag icon={<CameraGlyph width={2} />} label={t('chat.rich.clipRequestTag')} />
      <CardRow
        image={exercise?.coverImageUrl ?? exercise?.posterUrl}
        title={name}
        meta={muscle(exercise?.primaryMuscleGroup)}
      />
      {!available ? (
        <div className={styles.cardGone}>{t('chat.rich.clipRequestGone')}</div>
      ) : message.mine && customerId !== null ? (
        <Link className={styles.cta} to="/klienci/$clientId" params={{ clientId: String(customerId) }} search={{ tab: 'forma' }}>
          {t('chat.rich.viewClientClips')}
          <ArrowGlyph />
        </Link>
      ) : null}
    </div>
  );
}

function ClipCard({ message, onOpenClip }: { readonly message: ChatMessage; readonly onOpenClip: (clipId: number) => void }) {
  const { t, i18n } = useTranslation();
  const clip = message.wire.exerciseClip;
  const poster = useSignedUrl(null, clip?.posterUrl, 'poster');
  const name = clip?.exercise?.name ?? message.wire.content ?? '';
  const clipId = clip?.clipId;
  const available = clip?.clipAvailable !== false && typeof clipId === 'number' && clipId > 0;
  const performed =
    typeof clip?.performedOn === 'string'
      ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long' }).format(new Date(clip.performedOn))
      : '';
  const meta = performed === '' ? '' : t('web:chat.cards.clipMeta', { date: performed });
  const open = () => {
    if (available) onOpenClip(clipId);
  };
  return (
    <div className={styles.card} aria-label={t('chat.rich.clipCardA11y', { title: name })} role="group">
      <CardTag icon={<PlayGlyph />} label={t('chat.rich.clipTag')} />
      <button className={cx(styles.cardRow, styles.clipRow)} type="button" onClick={open} disabled={!available} aria-label={t('chat.rich.watchClip')}>
        <span className={cx(styles.thumb, styles.clipThumb)}>
          {poster.url !== null ? (
            <img className={String(photoBox.photo)} src={poster.url} alt="" decoding="async" onError={poster.onError} />
          ) : null}
          <span className={styles.playMini} aria-hidden="true">
            <PlayGlyph size={12} />
          </span>
        </span>
        <span className={styles.cardText}>
          <span className={styles.cardTitle}>{name}</span>
          {meta === '' ? null : <span className={styles.cardMeta}>{meta}</span>}
        </span>
      </button>
      {available ? (
        <button className={styles.cta} type="button" onClick={open}>
          {t('web:chat.cards.openInAnalysis')}
          <ArrowGlyph />
        </button>
      ) : (
        <div className={styles.cardGone}>{t('chat.rich.clipGone')}</div>
      )}
    </div>
  );
}
